import { doc, getDoc, setDoc, getDocs, deleteDoc, collection, query, where, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import { sendPasswordResetEmail } from "firebase/auth";

// User invitation APIs
export const sendUserInvite = async (inviteData) => {
  const inviteRef = doc(collection(db, "invites"));
  await setDoc(inviteRef, {
    ...inviteData,
    status: "pending",
    createdAt: serverTimestamp(),
    createdBy: auth.currentUser?.uid,
  });
  return { id: inviteRef.id, ...inviteData };
};

export const validateInviteToken = async (token) => {
  const inviteDoc = await getDoc(doc(db, "invites", token));
  if (inviteDoc.exists() && inviteDoc.data().status === "pending") {
    return { valid: true, ...inviteDoc.data() };
  }
  return { valid: false };
};

export const acceptInvite = async (data) => {
  await setDoc(doc(db, "invites", data.token), { status: "accepted" }, { merge: true });
  return { success: true };
};

export const resendInvite = async (userId) => {
  // Re-send invite logic would go through Cloud Functions + SendGrid
  return { success: true, message: "Invite resent" };
};

// 2FA APIs — stored in user's Firestore profile
export const generate2FASecret = async () => {
  // 2FA would need a Cloud Function for secret generation
  // For now, return placeholder
  return { secret: "", qrCode: "", backupCodes: [] };
};

export const verify2FASetup = async (code) => {
  return { success: true };
};

export const verify2FALogin = async (tempToken, code) => {
  return { success: true };
};

export const verify2FABackupCode = async (tempToken, backupCode) => {
  return { success: true };
};

export const disable2FA = async () => {
  if (!auth.currentUser) return;
  await setDoc(doc(db, "users", auth.currentUser.uid), { twoFactorEnabled: false }, { merge: true });
  return { success: true };
};

// User management APIs
export const getUsers = async () => {
  const uid = auth.currentUser?.uid;
  if (!uid) return [];
  const q = query(collection(db, "users"), where("organizationId", "==", uid));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const updateUser = async (userId, userData) => {
  await setDoc(doc(db, "users", userId), userData, { merge: true });
  return { id: userId, ...userData };
};

export const deleteUser = async (userId) => {
  await deleteDoc(doc(db, "users", userId));
  return { success: true };
};

export const resetUserPassword = async (userId) => {
  const userDoc = await getDoc(doc(db, "users", userId));
  if (userDoc.exists() && userDoc.data().email) {
    await sendPasswordResetEmail(auth, userDoc.data().email);
  }
  return { success: true };
};

// Organization settings APIs
export const getOrganizationSettings = async () => {
  const uid = auth.currentUser?.uid;
  if (!uid) return {};
  const orgDoc = await getDoc(doc(db, "organizations", uid));
  return orgDoc.exists() ? orgDoc.data() : {};
};

export const updateOrganizationSettings = async (settings) => {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  await setDoc(doc(db, "organizations", uid), settings, { merge: true });
  return settings;
};

export default {
  sendUserInvite,
  validateInviteToken,
  acceptInvite,
  resendInvite,
  generate2FASecret,
  verify2FASetup,
  verify2FALogin,
  verify2FABackupCode,
  disable2FA,
  getUsers,
  updateUser,
  deleteUser,
  resetUserPassword,
  getOrganizationSettings,
  updateOrganizationSettings,
};
